import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Box, TextField, Button,FormControl } from "@mui/material";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const CheckInOutRecords = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const accessToken = JSON.parse(localStorage.getItem('emsUser'))?.user?.stsTokenManager?.accessToken;
  const getFormattedDate = (date) => date.toISOString().split("T")[0];

  const CustomTextField = styled(TextField)({
    "& input[type='date']::-webkit-calendar-picker-indicator": {
      filter: "invert(100%)",
      cursor: "pointer",
    },
  });

  useEffect(() => {
    const today = new Date();
    const pastDate = new Date();
    pastDate.setDate(today.getDate() - 7);

    setStartDate(getFormattedDate(pastDate));
    setEndDate(getFormattedDate(today));

    fetchData(getFormattedDate(pastDate), getFormattedDate(today));
  }, []);


const calculateDuration = (record) => {
  let totalDuration = 0;

  for (let i = 1; i <= 5; i++) {
    const checkInTime = record[`check_in_${i}`];
    const checkOutTime = record[`check_out_${i}`];

    if (checkInTime && checkOutTime) {
      const checkInDate = new Date(`1970-01-01T${checkInTime}`);
      const checkOutDate = new Date(`1970-01-01T${checkOutTime}`);

      if (!isNaN(checkInDate) && !isNaN(checkOutDate) && checkOutDate > checkInDate) {
        totalDuration += (checkOutDate - checkInDate) / (1000 * 60 * 60); // Convert ms to hours
      }
    }
  }

  return totalDuration.toFixed(2);
};




  const fetchData = async (start, end) => {
    const email = localStorage.getItem("UserEmail");

    if (!email) {
      alert("User email not found. Please log in.");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL_LMS}/get-checkin-checkout-range`, {
        method: "POST",
        headers: { "Content-Type": "application/json",'Authorization': `Bearer ${accessToken}` },
        body: JSON.stringify({ email, start_date: start, end_date: end }),
      });

      const data = await response.json();

      if (data && data.check_in_out) {
        const transformedRows = data.check_in_out.map((record, index) => {
          const filledRecords = [...record.records];

          while (filledRecords.length < 5) {
            filledRecords.push({ check_in: "", check_out: "" });
          }

          const row = {
            id: index,
            date: record.date,
            ...filledRecords.reduce((acc, rec, i) => {
              acc[`check_in_${i + 1}`] = rec.check_in || "";
              acc[`check_out_${i + 1}`] = rec.check_out || "";
              return acc;
            }, {}),
          };

          row.duration = calculateDuration(row);
          return row;
        });

        setRows(transformedRows);
      }
    } catch (error) {
      console.error("Error fetching check-in/out data:", error);
    }
  };

  const columns = [
    { field: "date", headerName: "Date", flex: 1 },
    ...Array.from({ length: 5 }, (_, i) => [
      { field: `check_in_${i + 1}`, headerName: `Check-In ${i + 1}`, flex: 1 },
      { field: `check_out_${i + 1}`, headerName: `Check-Out ${i + 1}`, flex: 1 },
    ]).flat(),
    { field: "duration", headerName: "Total Duration (hrs)", flex: 1 },
  ];

  const any = localStorage.getItem("AccessLevel")

    

   const customInputSx = {
    borderRadius: "8px",
    backgroundColor: "transparent",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#aaa",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#aaa !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#aaa !important",
    },
  };

  const customLabelSx = (colors) => ({
    fontSize: "16.5px",
    fontWeight: "bold",
    color: colors.grey[100],
    letterSpacing: "-0.5px",
    "&.Mui-focused": {
      color: colors.grey[100],
    },
    "&.MuiInputLabel-shrink": {
      color: colors.grey[100],
    },
  });

  return (
    <Box m="20px" height="75vh">
      <Header title="Check-In / Check-Out Records" subtitle="Select a date range to filter records." />

      <Box display="flex" gap={2} mb={2}>
         <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormControl sx={{ width: 250 }}>
              <DatePicker
                label="From Date"
                value={startDate ? new Date(startDate) : null}
                onChange={(newValue) => {
                  if (newValue instanceof Date && !isNaN(newValue)) {
                    const formatted = newValue.toISOString().split("T")[0];
                    setStartDate(formatted);
                  } else {
                    setStartDate("");
                  }
                }}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    size: "medium",
                    InputLabelProps: {
                      shrink: true,
                      sx: customLabelSx(colors),
                    },
                    sx: {
                      ...customInputSx,
                        width: "100%",
                        input: {
                          color: colors.grey[100],
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "transparent",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1.5px solid #aaa !important", // ALWAYS show solid border
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "1.5px solid #aaa !important",
                        },
                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "1.5px solid #aaa !important",
                        },
                        "& .MuiSvgIcon-root": {
                          color: colors.grey[100],
                        },
                    },
                  },
                }}
              />
            </FormControl>

            <FormControl sx={{ width: 250 }}>
              <DatePicker
                label="To Date"
                value={endDate ? new Date(endDate) : null}
                onChange={(newValue) => {
                  if (newValue instanceof Date && !isNaN(newValue)) {
                    const formatted = newValue.toISOString().split("T")[0];
                    setEndDate(formatted);
                  } else {
                    setEndDate("");
                  }
                }}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    size: "medium",
                    InputLabelProps: {
                      shrink: true,
                      sx: customLabelSx(colors),
                    },
                    sx: {
                      ...customInputSx,
                      width: "100%",
                      input: {
                        color: colors.grey[100],
                      },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "transparent",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "1.5px solid #aaa !important", // ALWAYS show solid border
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "1.5px solid #aaa !important",
                      },
                      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "1.5px solid #aaa !important",
                      },
                      "& .MuiSvgIcon-root": {
                        color: colors.grey[100],
                      },
                    },
                  },
                }}
              />
            </FormControl>
          </LocalizationProvider>
        <Button
          variant="contained"
          onClick={() => fetchData(startDate, endDate)}
          sx={{
            backgroundColor: colors.greenAccent[600],  
            color: colors.grey[100],                 
            fontWeight: "bold",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: colors.blueAccent[700], 
            },
          }}
        >
          Fetch Records
        </Button>
      </Box>

      <Box height="75vh" sx={{
        "& .MuiDataGrid-root": { border: "none" },
        "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700] },
        "& .MuiDataGrid-footerContainer": { backgroundColor: colors.blueAccent[700] },
      }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          components={{ Toolbar: GridToolbar }}
          disableSelectionOnClick
          autoHeight
        />
      </Box>
    </Box>
  );
};

export default CheckInOutRecords;